"use client";

import { Mail, MessageCircle } from "lucide-react";
import Image from "next/image";
import { useIntlayer } from "next-intlayer";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function FacebookIcon({ size = 20, className }: { size?: number; className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

const contacts = [
  {
    href: "mailto:learn@somemath.com",
    label: "Email",
    value: "learn@somemath.com",
    icon: Mail,
    external: false,
  },
  {
    href: "https://line.me/ti/p/@somemath",
    label: "Line",
    value: "@somemath",
    icon: MessageCircle,
    external: true,
  },
  {
    href: "https://www.facebook.com/somemath",
    label: "Facebook",
    value: "somemath",
    icon: FacebookIcon,
    external: true,
  },
];

export default function ContactModal() {
  const [open, setOpen] = useState(false);
  const t = useIntlayer("footer");

  useEffect(() => {
    const key = "somemath-contacts-shown";
    if (typeof sessionStorage !== "undefined" && !sessionStorage.getItem(key)) {
      const timer = setTimeout(() => {
        setOpen(true);
        sessionStorage.setItem(key, "true");
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle className="sr-only">Contact SomeMath</DialogTitle>

        <div className="flex flex-col items-center text-center gap-3 pt-2">
          <Image src="/logo.svg" alt="" width={40} height={40} />
          <span className="text-lg font-semibold text-foreground">SomeMath</span>
        </div>

        <p className="text-sm leading-relaxed text-center text-muted-foreground italic">
          {t.quote}
        </p>
        <p className="text-sm leading-relaxed text-center text-muted-foreground">{t.tagline}</p>

        <div className="border-t border-border pt-5 mt-3">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase mb-5 text-center text-[#0193cd]">
            {t.contactHeading}
          </p>
          <div className="flex flex-col gap-3">
            {contacts.map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.label}
                  href={c.href}
                  target={c.external ? "_blank" : undefined}
                  rel={c.external ? "noopener noreferrer" : undefined}
                  className="group flex items-center gap-4 rounded-xl bg-muted p-4 transition-all hover:-translate-y-0.5 hover:bg-muted/80 hover:ring-1 hover:ring-[#0193cd]/20"
                >
                  <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#0193cd] text-white">
                    <Icon size={20} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{c.label}</p>
                    <p className="text-sm text-muted-foreground truncate">{c.value}</p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
